import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ViewAgendaOutlinedIcon from "@mui/icons-material/ViewAgendaOutlined";
import {SurveyFormat} from "crqlar-react-common/dist/models/survey/survey.model";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {alpha} from "@mui/material/styles";
import React from "react";
import {useTranslation} from "react-i18next";
import {ElementChip} from "./base/ElementChip";

export interface SurveyFormatDisplay {
    label: string;
    color: string;
    Icon: React.ElementType;
}

const SURVEY_FORMAT_CONFIG: Record<SurveyFormat, SurveyFormatDisplay> = {
    [SurveyFormat.SINGLE_PAGE]: {
        label: 'All on one page',
        color: CommonThemeColors.sys.info.main,
        Icon: DescriptionOutlinedIcon,
    },
    [SurveyFormat.MULTI_PAGE]: {
        label: 'One question per page',
        color: CommonThemeColors.sys.warning.main,
        Icon: AutoStoriesOutlinedIcon,
    },
    [SurveyFormat.SECTIONS]: {
        label: 'One section per page',
        color: CommonThemeColors.sys.success.main,
        Icon: ViewAgendaOutlinedIcon,
    },
};

// A survey saved without a format runs as a single page, so it reads as one everywhere.
export const getSurveyFormatDisplay = (
    format: SurveyFormat | undefined,
): SurveyFormatDisplay => SURVEY_FORMAT_CONFIG[format ?? SurveyFormat.SINGLE_PAGE];

export const SURVEY_FORMAT_LABEL_KEYS: Record<SurveyFormat, string> = {
    [SurveyFormat.SINGLE_PAGE]: SURVEY_FORMAT_CONFIG[SurveyFormat.SINGLE_PAGE].label,
    [SurveyFormat.MULTI_PAGE]: SURVEY_FORMAT_CONFIG[SurveyFormat.MULTI_PAGE].label,
    [SurveyFormat.SECTIONS]: SURVEY_FORMAT_CONFIG[SurveyFormat.SECTIONS].label,
};

interface Props {
    format: SurveyFormat | undefined;
    asPrint?: boolean;
}

export function SurveyFormatElement({format, asPrint = false}: Props) {
    const {t} = useTranslation();

    const {label, color, Icon} = getSurveyFormatDisplay(format);
    const text = t(label);

    if (asPrint) {
        return <>{text}</>;
    }

    return (
        <ElementChip
            text={text}
            startIcon={<Icon sx={{fontSize: 15, color}}/>}
            borderColor={alpha(color, 0.34)}
            backgroundColor={alpha(color, 0.06)}
        />
    );
}

export default SurveyFormatElement;
